import { type GeoLocation } from "@src/appV2/Location";
import {
  type ClaimShiftRequest,
  useClaimShift,
} from "@src/appV2/OpenShifts/ShiftAction/api/useClaimShift";
import { useShiftDiscoverySearchMode } from "@src/appV2/redesign/ShiftDiscovery/useShiftDiscoverySearchMode";
import { useCallback, useState } from "react";

interface FailedShift {
  shiftId: string;
  error: unknown;
}

interface BookDoubleShiftsResult {
  bookedShiftIds: string[];
  failedShifts: FailedShift[];
}

interface ShiftToBook {
  shiftId: string;
  offerId: string;
}

export function useBookDoubleShifts() {
  const { mutateAsync: claimShift } = useClaimShift();
  const searchMode = useShiftDiscoverySearchMode();
  const [isBooking, setIsBooking] = useState(false);

  const bookDoubleShifts = useCallback(
    async (shifts: ShiftToBook[], geoLocation?: GeoLocation): Promise<BookDoubleShiftsResult> => {
      setIsBooking(true);

      try {
        const claimRequests: ClaimShiftRequest[] = shifts.map((shift) => ({
          shiftId: shift.shiftId,
          offerId: shift.offerId,
          searchMode,
          latitude: geoLocation?.latitude,
          longitude: geoLocation?.longitude,
        }));

        const results = await Promise.allSettled(
          claimRequests.map(async (request) => {
            await claimShift(request);
            return request.shiftId;
          })
        );

        const bookedShiftIds: string[] = [];
        const failedShifts: FailedShift[] = [];

        for (const [index, result] of results.entries()) {
          if (result.status === "fulfilled") {
            bookedShiftIds.push(result.value);
          } else {
            failedShifts.push({
              shiftId: claimRequests[index].shiftId,
              error: result.reason,
            });
          }
        }

        return { bookedShiftIds, failedShifts };
      } finally {
        setIsBooking(false);
      }
    },
    [claimShift, searchMode]
  );

  return {
    bookDoubleShifts,
    isBooking,
  };
}
